import React, { useState } from 'react';
import get from 'lodash/get';

// Constants
import Colors from '../../../colors/ColorConstants';

// Types
import { Color } from '../../../colors/Colors.types';

// Components
import WpText, { TextElement, Variant } from '../WpText';
import { WpTextField } from 'Wpds/components/text-field';
import { WpDivider } from 'Wpds/components/divider';
import { WpContainer, WpContainerBody } from 'Wpds/components/container';
import { WpCheckbox } from 'Wpds/components/checkbox';
import { WpDropdown, WpDropdownToggle, WpDropdownMenu, WpDropdownMenuItem } from '../../dropdown';
import { WpButton } from 'Wpds/components/button';
import { WpIcon } from 'Wpds/components/icon';
import { WpTabs, WpTab } from 'Wpds/components/tabs';
import { WpRichTextInput } from 'Wpds/components/rich-text';
import { WpDataTableV2 } from 'Wpds/components/table-v2';
import { WpBadge } from 'Wpds/components/badge';
import { WpLink } from 'Wpds/components/link';
import { WpFormLayout } from 'Wpds/components/form';

import './Text.docs.scss';

const TextDocs = () => {
  const [exampleText, setExampleText] = useState(
    'The quick brown fox jumps over the lazy dog. He lands head first on a rotting maple log. Knocked unconscious, fox sleeps with shallow breath until the lazy dog awakes and worries him to death.'
  );
  const [tag, setTag] = useState<TextElement>('p');
  const [bold, setBold] = useState<boolean>(false);
  const [italic, setItalic] = useState<boolean>(false);
  const [caps, setCaps] = useState<boolean>(false);
  const [truncate, setTruncate] = useState<boolean>(false);
  const [tab, setTab] = useState<'ts' | 'html'>('ts');
  const [color, setColor] = useState<Color>('gray-900');

  const tagTypes: TextElement[] = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'span'];
  const variantTypes: Variant[] = [
    'titleLg',
    'titleMd',
    'titleSm',
    'titleXs',
    'bodyXxl',
    'bodyXl',
    'bodyLg',
    'bodyMd',
    'bodySm',
    'bodyXs',
  ];

  const baseClasName = 'TextDocs';

  return (
    <>
      <div style={{ marginBottom: '2rem' }}>
        <WpText tag="h5" variant="titleSm">
          Basic Useage
        </WpText>
        <WpContainer>
          <WpContainerBody>
            <div style={{ margin: '2rem 0' }}>
              <WpText tag="h1" variant="titleLg">
                Hello, I&apos;m an H1 Title!
              </WpText>
              <WpText>Hello, I&apos;m a paragrah text!</WpText>
              <WpText>
                Learn how to use the{' '}
                <WpText tag="span" bold>
                  WpText
                </WpText>{' '}
                component below!
              </WpText>
            </div>
            <WpRichTextInput
              value={{
                blocks: [
                  {
                    key: 'et3uo',
                    text: "import React from 'react';",
                    type: 'unstyled',
                    depth: 0,
                    inlineStyleRanges: [],
                    entityRanges: [],
                    data: {},
                  },
                  {
                    key: '3rq2d',
                    text: "import { WpText } from 'Wpds/components/text';",
                    type: 'unstyled',
                    depth: 0,
                    inlineStyleRanges: [],
                    entityRanges: [],
                    data: {},
                  },
                  {
                    key: '3tb6g',
                    text: '',
                    type: 'unstyled',
                    depth: 0,
                    inlineStyleRanges: [],
                    entityRanges: [],
                    data: {},
                  },
                  {
                    key: 'db9mv',
                    text: 'const Example = () => {',
                    type: 'unstyled',
                    depth: 0,
                    inlineStyleRanges: [],
                    entityRanges: [],
                    data: {},
                  },
                  {
                    key: '62dof',
                    text: ' return (',
                    type: 'unstyled',
                    depth: 0,
                    inlineStyleRanges: [],
                    entityRanges: [],
                    data: {},
                  },
                  {
                    key: '134ts',
                    text: ' <>',
                    type: 'unstyled',
                    depth: 0,
                    inlineStyleRanges: [],
                    entityRanges: [],
                    data: {},
                  },
                  {
                    key: '9c77b',
                    text: ' <WpText tag="h1" variant="titleLg">Hello, I\'m an H1 Title!</WpText>',
                    type: 'unstyled',
                    depth: 0,
                    inlineStyleRanges: [],
                    entityRanges: [],
                    data: {},
                  },
                  {
                    key: '2fhcb',
                    text: " <WpText>Hello, I'm a paragrah text!</WpText>",
                    type: 'unstyled',
                    depth: 0,
                    inlineStyleRanges: [],
                    entityRanges: [],
                    data: {},
                  },
                  {
                    key: '2jmpf',
                    text:
                      " <WpText>Learn how to use the{' '}<WpText tag=\"span\" bold>WpText</WpText>{' '}component below!</WpText>",
                    type: 'unstyled',
                    depth: 0,
                    inlineStyleRanges: [],
                    entityRanges: [],
                    data: {},
                  },
                  {
                    key: '3cr83',
                    text: ' </>',
                    type: 'unstyled',
                    depth: 0,
                    inlineStyleRanges: [],
                    entityRanges: [],
                    data: {},
                  },
                  {
                    key: 'f7eka',
                    text: ' )',
                    type: 'unstyled',
                    depth: 0,
                    inlineStyleRanges: [],
                    entityRanges: [],
                    data: {},
                  },
                  {
                    key: 'fs9k9',
                    text: '};',
                    type: 'unstyled',
                    depth: 0,
                    inlineStyleRanges: [],
                    entityRanges: [],
                    data: {},
                  },
                ],
                entityMap: {},
              }}
              options={{ showToolbar: false, backgroundColor: Colors.gray[50] }}
              disabled
              height=""
            />
          </WpContainerBody>
        </WpContainer>
      </div>
      <WpText tag="h5" variant="titleSm">
        API
      </WpText>
      <WpDataTableV2
        tableId="prop-table"
        columns={[
          {
            label: 'Prop',
            selector: row => row.name,
            component: ({ row }) => {
              return <b>{row.name}</b>;
            },
            isSortable: false,
          },
          {
            label: 'Description',
            selector: row => row.description,
            isSortable: false,
          },
          {
            label: 'Type',
            selector: row => row.type,
            component: ({ row }) => {
              return (
                <span>
                  {row.type && row.type}
                  {row?.typeOptions && typeof row?.typeOptions === 'object' && (
                    <>
                      {row.type && <>&nbsp;&nbsp;</>}
                      {row.typeOptions.map((option, index) => (
                        <React.Fragment key={option}>
                          <WpBadge>{option}</WpBadge>
                          {index < row.typeOptions.length - 1 && (
                            <span style={{ color: 'lightgray' }}>&nbsp;|&nbsp;</span>
                          )}
                        </React.Fragment>
                      ))}
                    </>
                  )}
                </span>
              );
            },
            isSortable: false,
          },
          {
            label: 'Default',
            selector: row => row.default,
            component: ({ row }) => {
              if (row.default === '-') return row.default;
              return <WpBadge>{row.default}</WpBadge>;
            },
            isSortable: false,
          },
          {
            label: 'Required',
            selector: row => row.required,
            isSortable: false,
          },
        ]}
        data={[
          {
            name: 'tag',
            description: 'HTML element to render',
            typeOptions: tagTypes,
            default: 'p',
            required: '-',
          },
          {
            name: 'variant',
            description: 'controls the size of the text',
            typeOptions: variantTypes,
            default: 'bodyMd',
            required: '-',
          },
          {
            name: 'bold',
            description: 'applies bold styling',
            type: 'boolean',
            default: 'false',
            required: '-',
          },
          {
            name: 'italic',
            description: 'applies italic styling',
            type: 'boolean',
            default: 'false',
            required: '-',
          },
          {
            name: 'truncate',
            description: 'truncates text with ellipsis',
            type: 'boolean',
            default: 'false',
            required: '-',
          },
          {
            name: 'caps',
            description: 'applies capital styling',
            type: 'boolean',
            default: 'false',
            required: '-',
          },
          {
            name: 'id',
            description: 'HTML id applied to element',
            type: 'string',
            default: '-',
            required: '-',
          },
          {
            name: 'qaTest',
            description: 'test id applied to element',
            type: 'string',
            default: '-',
            required: '-',
          },
          {
            name: 'color',
            description: 'applies a color directly to the text directly within TSX.',
            type: (
              <>
                string of type Colors.type (<WpLink href="/wpds/colors">see Colors</WpLink>)
              </>
            ),
            default: 'gray-900',
            required: '-',
          },
        ]}
      />
      <WpDivider padding={32} />
      <WpText tag="h5" variant="titleSm">
        Playground
      </WpText>
      <WpFormLayout>
        <WpTextField id="sample-text-input" label="Sample Text" value={exampleText} onChange={setExampleText} />
        <div style={{ width: '300px' }}>
          <WpTextField id="color" label="Color" value={color} onChange={value => setColor(value as Color)} />
        </div>
      </WpFormLayout>
      <WpCheckbox id="bold" label="bold" checked={bold} onChange={() => setBold(!bold)} />
      <WpCheckbox id="italic" label="italic" checked={italic} onChange={() => setItalic(!italic)} />
      <WpCheckbox id="truncate" label="truncate" checked={truncate} onChange={() => setTruncate(!truncate)} />
      <WpCheckbox id="caps" label="caps" checked={caps} onChange={() => setCaps(!caps)} />
      <WpDropdown>
        <WpDropdownToggle>
          <WpButton variant="link" size="lg">
            tag: {tag}
            <WpIcon name="angle-down" />
          </WpButton>
        </WpDropdownToggle>
        <WpDropdownMenu>
          {tagTypes.map(tag => (
            <WpDropdownMenuItem onClick={() => setTag(tag)} key={tag}>
              {tag}
            </WpDropdownMenuItem>
          ))}
        </WpDropdownMenu>
      </WpDropdown>
      <WpDivider padding={32} />
      {variantTypes.map(variant => (
        <WpContainer className={`${baseClasName}__container`} key={variant}>
          <WpContainerBody>
            <WpText variant="bodyMd" bold>
              {variant}
            </WpText>
            <WpText
              tag={tag}
              variant={variant}
              color={color}
              bold={bold}
              italic={italic}
              truncate={truncate}
              caps={caps}
            >
              {exampleText}
            </WpText>
          </WpContainerBody>
          <WpContainerBody className={`${baseClasName}__container__tab-wrapper`}>
            <div className={`${baseClasName}__container__tabs`}>
              {' '}
              <WpTabs activeTab={tab} onTabClick={setTab}>
                <WpTab tabId="ts">TS</WpTab>
                <WpTab tabId="html">HTML</WpTab>
              </WpTabs>
            </div>
            {tab === 'ts' && (
              <div style={{ margin: '1.5rem 0' }}>{`<WpText tag="${tag}" variant="${variant}"${
                color !== 'gray-900' ? `color="${color}"` : ''
              }${bold ? ' bold' : ''}${italic ? ' italic' : ''}${caps ? ' caps' : ''}${
                truncate ? ' truncate' : ''
              }>${exampleText}</WpText>`}</div>
            )}
            {tab === 'html' && (
              <div style={{ margin: '1.5rem 0' }}>{`              <${tag} class="WpText WpText--${variant}${
                bold ? ' WpText--bold' : ''
              }${italic ? ' WpText--italic' : ''}${caps ? ' WpText--caps' : ''}${
                truncate ? ' WpText--truncate' : ''
              }" style="color: ${get(
                Colors,
                `${color?.split('-')[0]}.${color?.split('-')[1]}`
              )};">${exampleText}</${tag}>`}</div>
            )}
          </WpContainerBody>
        </WpContainer>
      ))}
    </>
  );
};

export default TextDocs;
